import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { reserveCart, releaseCart } from "@/lib/reservation";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { isReadOnlyGuest } from "@/lib/roles";

const KEY = "mpower-cart-v1";
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const readOnly = isReadOnlyGuest(user);
  useEffect(() => { if (user?.role === "distributing_partner") setItems([]); }, [user?.role]);
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  const [context, setContext] = useState(() => localStorage.getItem(KEY + "-ctx") || "retail");

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(KEY + "-ctx", context); }, [context]);

  // Sync the server-side 15-minute reservation whenever the cart changes.
  const reserveTimer = useRef(null);
  useEffect(() => {
    if (reserveTimer.current) clearTimeout(reserveTimer.current);
    if (readOnly) return undefined;
    reserveTimer.current = setTimeout(async () => {
      if (!items.length) { releaseCart(); return; }
      try {
        const res = await reserveCart(items);
        if (res?.conflicts?.length) {
          toast.warning("Some items had limited stock and were partially reserved.");
        }
      } catch { /* best-effort; timer badge still reflects localStorage */ }
    }, 400);
    return () => reserveTimer.current && clearTimeout(reserveTimer.current);
  }, [items, readOnly]);

  // When the global countdown badge expires, clear this cart too.
  useEffect(() => {
    const onExpired = () => setItems([]);
    window.addEventListener("cart-expired", onExpired);
    return () => window.removeEventListener("cart-expired", onExpired);
  }, []);

  const add = (product, qty = 1, bundleMeta = null) => {
    if (readOnly) return;
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.product_id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        // Preserve existing bundle metadata unless this add call provides new bundle info
        next[idx] = {
          ...next[idx],
          qty: next[idx].qty + qty,
          ...(bundleMeta ? { bundle_id: bundleMeta.bundle_id, bundle_name: bundleMeta.bundle_name, bundle_unit_price: bundleMeta.bundle_unit_price } : {}),
        };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          sku: product.sku,
          image_url: product.image_url,
          retail_price: Number(bundleMeta?.bundle_unit_price ?? product.retail_price ?? 0),
          wholesale_price: Number(product.wholesale_price || 0),
          warehouse_stock: Number(product.warehouse_stock || 0),
          qty,
          ...(bundleMeta ? { bundle_id: bundleMeta.bundle_id, bundle_name: bundleMeta.bundle_name, bundle_unit_price: bundleMeta.bundle_unit_price } : {}),
        },
      ];
    });
  };

  // Add an entire bundle in one call. Splits the bundle_price evenly across the
  // bundled products so line items ring up at the discounted rate but still show
  // as individual SKUs (matches the auditability requirement).
  const addBundle = (bundle) => {
    if (readOnly) return;
    const products = bundle?.products || [];
    if (!products.length) return;
    const bundlePrice = Number(bundle.bundle_price || 0) || products.reduce((s, p) => s + Number(p.retail_price || 0), 0);
    const perItem = +(bundlePrice / products.length).toFixed(2);
    products.forEach((p) => {
      add(p, 1, {
        bundle_id: bundle.id,
        bundle_name: bundle.name,
        bundle_unit_price: perItem,
      });
    });
  };

  const setQty = (product_id, qty) => {
    if (readOnly) return;
    setItems((prev) =>
      qty <= 0
        ? prev.filter((x) => x.product_id !== product_id)
        : prev.map((x) => (x.product_id === product_id ? { ...x, qty } : x))
    );
  };
  const remove = (product_id) => { if (!readOnly) setItems((prev) => prev.filter((x) => x.product_id !== product_id)); };
  const clear = () => { if (!readOnly) setItems([]); };

  const price = (it) => (context === "wholesale" ? it.wholesale_price : it.retail_price);
  const total = useMemo(
    () => items.reduce((s, it) => s + (price(it) || 0) * it.qty, 0),
    [items, context]
  );
  const count = useMemo(() => items.reduce((s, it) => s + it.qty, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, context, setContext, add, addBundle, setQty, remove, clear, total, count, price, readOnly }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
